import Papa from "papaparse";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import JsPDF from "jspdf";
import "jspdf-autotable";
import { tokens } from "../../../public/tokens";
import { conditionColors } from "./Ohs.styles";

/**
 * A design-token hex colour as exceljs wants it (ARGB, fully opaque).
 * @param {string} hexColour e.g. "#233064"
 * @returns {string} e.g. "FF233064"
 */
function toExcelColour(hexColour) {
    return `FF${hexColour.replace("#", "").toUpperCase()}`;
}

const { brand, neutral } = tokens.colors;
const excelBorderSide = { style: "thin", color: { argb: toExcelColour(neutral.grey["300"]) } };
// Excel export colours - all from the design tokens; tones match the on-screen condition badges.
const EXCEL_COLOURS = {
    heading: {
        fill: toExcelColour(brand["aie-blue"]["500"]),
        font: toExcelColour(neutral.white),
        subFont: toExcelColour(brand["aie-blue"]["800"]),
    },
    groupHeading: { fill: toExcelColour(brand["aie-blue"]["400"]), subFill: toExcelColour(brand["aie-blue"]["100"]) },
    groupHeadingAlternate: { fill: toExcelColour(brand["aie-blue"]["700"]), subFill: toExcelColour(brand["aie-blue"]["200"]) },
    border: { top: excelBorderSide, bottom: excelBorderSide, left: excelBorderSide, right: excelBorderSide },
    tones: Object.fromEntries(
        Object.entries({ ...conditionColors, flagged: conditionColors.damaged })
            .map(([toneKey, toneColours]) => [toneKey, { fill: toExcelColour(toneColours.background), font: toExcelColour(toneColours.color) }])
    ),
};

/**
 * Turns an OHS API error into something readable - 422s carry either a string or validator messages.
 * @param {Object} error Axios error.
 * @param {string} fallback Message when the API gave none.
 * @returns {string} Message to show the user.
 */
export function getOhsErrorMessage(error, fallback) {
    const apiMessage = error?.response?.data?.message;
    if (Array.isArray(apiMessage)) {
        return apiMessage.map((validationMessage) => validationMessage.message).join(" ");
    }

    return typeof apiMessage === "string" ? apiMessage : fallback;
}

/**
 * A user carried on a row with prefixed fields (assigned_Firstname, opened_by_Firstname, ...), in the
 * shape OhsUserCell expects - prefixes let one row carry several users.
 * @param {Object} row Box or report row.
 * @param {string} prefix e.g. assigned | alternate | opened_by
 * @param {number} userId That user's cms_users.UserID on the row.
 * @returns {Object|null} User, or null when there's no user.
 */
export function getPrefixedUser(row, prefix, userId) {
    if (!userId) {
        return null;
    }

    return {
        user_id: userId,
        Firstname: row[`${prefix}_Firstname`],
        Lastname: row[`${prefix}_Lastname`],
        Email: row[`${prefix}_Email`],
        Mobile: row[`${prefix}_Mobile`],
        picture_me: row[`${prefix}_picture_me`],
        user_activity_status: row[`${prefix}_user_activity_status`],
        user_status: row[`${prefix}_user_status`],
        role_name: row[`${prefix}_role_name`],
    };
}

/**
 * What the tag check on a box opening found, in words - for report cells and exports alike.
 * @param {Object} opening Report row with tag_serial_found, tag_serial_on_record, is_tag_mismatch, acknowledged_at.
 * @returns {Object} { label, isFlagged }
 */
export function describeTagCheck(opening) {
    const acknowledgedNote = opening.acknowledged_at ? " (acknowledged)" : "";
    // A null tag found means the person reported no tag on the box at all.
    if (opening.tag_serial_found === null) {
        return { label: `No tag on the box${acknowledgedNote}`, isFlagged: true };
    }
    if (opening.is_tag_mismatch) {
        return { label: `Mismatch - tag on record was ${opening.tag_serial_on_record}${acknowledgedNote}`, isFlagged: true };
    }

    return { label: "Matched the tag on record", isFlagged: false };
}

/**
 * Downloads an OHS report. Excel/CSV get one row per record; a column can belong to a group (e.g. an
 * item's Condition / Quantity / Expiry), which Excel shows as a merged heading above its sub-columns.
 * A cell can be { value, tone } - Excel colours it like the matching badge. The PDF gets one section
 * per record - a heading, a summary line, then a small table.
 * @param {Object} reportExport { fileType: xlsx|csv|pdf, fileName, columns: [{ label, group, width }], rows, frozenColumns, pdfSections: [{ title, summary, header, rows }] }
 */
export async function exportOhsReport({ fileType, fileName, columns, rows, frozenColumns = 0, pdfSections }) {
    const getCellValue = (reportCell) => (reportCell !== null && typeof reportCell === "object" ? reportCell.value : reportCell);

    if (fileType === "pdf") {
        const reportPdf = new JsPDF({ orientation: "landscape" });
        let nextY = 14;
        pdfSections.forEach((pdfSection) => {
            // Start a new page rather than splitting a section's heading from its table.
            if (nextY > reportPdf.internal.pageSize.getHeight() - 40) {
                reportPdf.addPage();
                nextY = 14;
            }
            reportPdf.setFontSize(11);
            reportPdf.text(pdfSection.title, 14, nextY);
            reportPdf.setFontSize(9);
            reportPdf.text(pdfSection.summary, 14, nextY + 6);
            reportPdf.autoTable({
                head: [pdfSection.header],
                body: pdfSection.rows,
                startY: nextY + 9,
                styles: { fontSize: 8, minCellHeight: 6, halign: "left" },
            });
            nextY = reportPdf.lastAutoTable.finalY + 12;
        });
        reportPdf.save(`${fileName}.pdf`);
        return;
    }

    if (fileType === "csv") {
        // CSV can't merge cells, so a grouped column carries its group in its own heading.
        const csvHeader = columns.map((column) => (column.group ? `${column.group} - ${column.label}` : column.label));
        const csvRows = rows.map((reportRow) => reportRow.map(getCellValue));
        const csvBlob = new Blob([Papa.unparse({ fields: csvHeader, data: csvRows })], { type: "text/csv;charset=utf-8," });
        const blobUrl = window.URL.createObjectURL(csvBlob);
        const downloadLink = document.createElement("a");
        downloadLink.download = fileName;
        downloadLink.href = blobUrl;
        downloadLink.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        return;
    }

    const hasGroups = columns.some((column) => column.group);
    const headerRowCount = hasGroups ? 2 : 1;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Report", {
        views: [{ state: "frozen", ySplit: headerRowCount, xSplit: frozenColumns }],
    });
    worksheet.columns = columns.map((column) => ({ width: column.width || 14 }));

    worksheet.addRow(columns.map((column, columnIndex) => {
        const isGroupStart = column.group && columns[columnIndex - 1]?.group !== column.group;
        return column.group ? (isGroupStart ? column.group : "") : column.label;
    }));
    if (hasGroups) {
        worksheet.addRow(columns.map((column) => (column.group ? column.label : "")));
    }

    // exceljs is 1-based. Each item group alternates between two shades so its sub-columns read as one block.
    let groupNumber = 0;
    columns.forEach((column, columnIndex) => {
        const excelColumn = columnIndex + 1;
        const isGroupStart = column.group && columns[columnIndex - 1]?.group !== column.group;
        if (isGroupStart) {
            groupNumber += 1;
        }
        const groupShade = groupNumber % 2 ? EXCEL_COLOURS.groupHeading : EXCEL_COLOURS.groupHeadingAlternate;
        const headingCells = [worksheet.getCell(1, excelColumn), ...(hasGroups ? [worksheet.getCell(2, excelColumn)] : [])];
        headingCells.forEach((headingCell, headingRowIndex) => {
            const isSubHeading = column.group && headingRowIndex === 1;
            headingCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isSubHeading ? groupShade.subFill : (column.group ? groupShade.fill : EXCEL_COLOURS.heading.fill) } };
            headingCell.font = { bold: true, color: { argb: isSubHeading ? EXCEL_COLOURS.heading.subFont : EXCEL_COLOURS.heading.font } };
            headingCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            headingCell.border = EXCEL_COLOURS.border;
        });

        if (!hasGroups) {
            return;
        }
        if (!column.group) {
            worksheet.mergeCells(1, excelColumn, 2, excelColumn);
        } else if (isGroupStart) {
            let groupEndIndex = columnIndex;
            while (columns[groupEndIndex + 1]?.group === column.group) {
                groupEndIndex += 1;
            }
            worksheet.mergeCells(1, excelColumn, 1, groupEndIndex + 1);
        }
    });

    // Excel never grows a row to fit wrapped text in merged cells, so size the group heading row to its longest heading.
    if (hasGroups) {
        const groupWidths = new Map();
        columns
            .filter((column) => column.group)
            .forEach((column) => groupWidths.set(column.group, (groupWidths.get(column.group) || 0) + (column.width || 14)));
        const headingLineCount = Math.max(1, ...[...groupWidths.entries()].map(([group, groupWidth]) => Math.ceil(group.length / (groupWidth * 0.9))));
        worksheet.getRow(1).height = headingLineCount * 15;
    }

    rows.forEach((reportRow) => {
        const excelRow = worksheet.addRow(reportRow.map(getCellValue));
        reportRow.forEach((reportCell, columnIndex) => {
            const excelCell = excelRow.getCell(columnIndex + 1);
            const cellTone = reportCell !== null && typeof reportCell === "object" ? EXCEL_COLOURS.tones[reportCell.tone] : null;
            excelCell.border = EXCEL_COLOURS.border;
            excelCell.alignment = { vertical: "top", wrapText: true };
            if (cellTone) {
                excelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: cellTone.fill } };
                excelCell.font = { bold: true, color: { argb: cellTone.font } };
            }
        });
    });

    const workbookBuffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${fileName}.xlsx`);
}

/**
 * Puts campus checklist questions (or a check's answers) in form order - grouped by section, in the
 * order each section first appears - and numbers them 1, 2, 3... across the whole list.
 * @param {Array<Object>} questions Rows with section and display_order.
 * @returns {Array<Object>} The same rows in form order, each with a number.
 */
export function numberChecklistQuestions(questions) {
    const sortedQuestions = [...questions].sort((first, second) => first.display_order - second.display_order);
    const sectionOrder = [...new Set(sortedQuestions.map((checklistQuestion) => checklistQuestion.section))];

    return sectionOrder
        .flatMap((section) => sortedQuestions.filter((checklistQuestion) => checklistQuestion.section === section))
        .map((checklistQuestion, questionIndex) => ({ ...checklistQuestion, number: questionIndex + 1 }));
}

/**
 * Every status an item has - it can be several at once (e.g. Low and Expired).
 * @param {Object} itemRow A check/stock row with is_low, is_expired, is_damaged and (live rows) is_expiring_soon.
 * @returns {Array<string>} Condition keys, worst first: expired, damaged, low, expiring_soon - or ["ok"].
 */
export function getItemStatuses(itemRow) {
    const itemStatuses = [
        itemRow.is_expired ? "expired" : null,
        itemRow.is_damaged ? "damaged" : null,
        itemRow.is_low ? "low" : null,
        itemRow.is_expiring_soon ? "expiring_soon" : null,
    ].filter(Boolean);

    return itemStatuses.length ? itemStatuses : ["ok"];
}
